import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, PackageCheck } from "lucide-react";

const num = (n) => Math.round(n).toLocaleString();
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
const lbl = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

export function PoReceiveDialog({ target, onOpenChange, onSaved }) {
  const po = target?.po;
  const [rows, setRows] = useState({});
  const [shipmentId, setShipmentId] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [allowOver, setAllowOver] = useState(false);
  const [idemKey, setIdemKey] = useState("");
  const [saving, setSaving] = useState(false);

  const openShipments = useMemo(() => (po?.shipments || []).filter((s) => !["cancelled"].includes(s.status)), [po]);

  useEffect(() => {
    if (!target) return;
    setIdemKey(uid());
    setShipmentId(target.shipment?.id || "");
    setLocation("");
    setNotes("");
    setAllowOver(false);
    const r = {};
    const shipLines = target.shipment?.lines || null;
    (po?.lines || []).forEach((l) => {
      const fromShip = shipLines?.find((x) => x.po_line_id === l.id);
      const suggested = fromShip ? Math.min(fromShip.qty, l.remaining || fromShip.qty) : l.remaining;
      r[l.id] = { accepted: suggested > 0 ? String(suggested) : "", damaged: "", rejected: "", note: "" };
    });
    setRows(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!po) return null;
  const setRow = (id, k, v) => setRows((p) => ({ ...p, [id]: { ...p[id], [k]: v.replace(/[^0-9.]/g, "") } }));
  const anyQty = Object.values(rows).some((r) => (Number(r.accepted) || 0) + (Number(r.damaged) || 0) + (Number(r.rejected) || 0) > 0);
  const anyOver = (po.lines || []).some((l) => (Number(rows[l.id]?.accepted) || 0) > (l.remaining || 0) + 1e-9);

  const post = async () => {
    setSaving(true);
    try {
      const payload = {
        shipment_id: shipmentId === "none" ? "" : shipmentId, location, notes,
        idempotency_key: idemKey, allow_over: allowOver,
        lines: (po.lines || []).map((l) => ({
          po_line_id: l.id,
          qty_accepted: Number(rows[l.id]?.accepted) || 0,
          qty_damaged: Number(rows[l.id]?.damaged) || 0,
          qty_rejected: Number(rows[l.id]?.rejected) || 0,
          note: rows[l.id]?.note || "",
        })).filter((x) => x.qty_accepted + x.qty_damaged + x.qty_rejected > 0),
      };
      const { data } = await api.post(`/purchase-orders/${po.id}/receipts`, payload);
      toast.success(`${data.receipt_number} posted — accepted quantities added to inventory`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to post receiving report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="po-receive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading tracking-tight"><PackageCheck className="h-5 w-5 text-[#002FA7]" /> Receive Shipment</DialogTitle>
          <DialogDescription>{po.po_number} · {po.supplier_name}. Only accepted quantities are added to usable inventory — damaged/rejected are recorded as discrepancies.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className={lbl}>Inbound Shipment</Label>
              <Select value={shipmentId || "none"} onValueChange={setShipmentId}>
                <SelectTrigger className="rounded-sm" data-testid="po-receive-shipment">
                  <span className="truncate text-sm">{shipmentId && shipmentId !== "none" ? (openShipments.find((s) => s.id === shipmentId)?.shipment_number || "Shipment") : "No linked shipment"}</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked shipment</SelectItem>
                  {openShipments.map((s) => (<SelectItem key={s.id} value={s.id}>{s.shipment_number} · {s.status.replace(/_/g, " ")}{s.tracking ? ` · ${s.tracking}` : ""}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Receiving Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={po.destination?.split("\n")[0] || "e.g. Main warehouse"} className="rounded-sm" data-testid="po-receive-location" />
            </div>
          </div>

          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-2">Item</th>
                  <th className="px-2 py-2 text-right">Remaining</th>
                  <th className="px-2 py-2 text-right">Accepted</th>
                  <th className="px-2 py-2 text-right">Damaged</th>
                  <th className="px-2 py-2 text-right">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {(po.lines || []).map((l, i) => {
                  const over = (Number(rows[l.id]?.accepted) || 0) > (l.remaining || 0) + 1e-9;
                  return (
                    <tr key={l.id} className="border-b border-slate-100 last:border-0" data-testid={`po-receive-row-${i}`}>
                      <td className="px-2 py-2">
                        <p className="font-medium text-slate-800">{l.name}</p>
                        <p className="text-[10px] text-slate-400">{num(l.received)} / {num(l.quantity)} {l.unit} received{l.custom ? " · custom item (no inventory link)" : ""}</p>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-500">{num(l.remaining)}</td>
                      <td className="px-2 py-2">
                        <Input value={rows[l.id]?.accepted || ""} onChange={(e) => setRow(l.id, "accepted", e.target.value)}
                          className={`h-8 w-24 rounded-sm text-right tabular-nums ${over ? "border-red-400" : ""}`} data-testid={`po-receive-accepted-${i}`} />
                      </td>
                      <td className="px-2 py-2"><Input value={rows[l.id]?.damaged || ""} onChange={(e) => setRow(l.id, "damaged", e.target.value)} placeholder="0" className="h-8 w-20 rounded-sm text-right tabular-nums" data-testid={`po-receive-damaged-${i}`} /></td>
                      <td className="px-2 py-2"><Input value={rows[l.id]?.rejected || ""} onChange={(e) => setRow(l.id, "rejected", e.target.value)} placeholder="0" className="h-8 w-20 rounded-sm text-right tabular-nums" data-testid={`po-receive-rejected-${i}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {anyOver && (
            <label className="flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="po-receive-over-warning">
              <input type="checkbox" checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} data-testid="po-receive-allow-over" />
              An accepted quantity exceeds what's remaining on the PO — authorize this over-receipt to post.
            </label>
          )}
          <div className="space-y-1"><Label className={lbl}>Notes (shortages, condition, seal #…)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" data-testid="po-receive-notes" /></div>
          <p className="text-xs text-slate-500">Posting is final and updates inventory instantly. Use "Reverse" on the receipt afterwards if a correction is needed.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="po-receive-cancel">Cancel</Button>
            <Button disabled={!anyQty || saving || (anyOver && !allowOver)} onClick={post} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="po-receive-post">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm &amp; Post Receipt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
